import { useMemo, useCallback, useRef, useEffect } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { getPubSubClient, eventToAvi } from '@confluence/pubsub-client';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { getEditorResolvedState } from '@confluence/comments-util/entry-points/stateResolver';
import { getSortedMarkerRefs } from '@confluence/comments-util';
import { useAnnotations } from '@confluence/annotation-provider-store';
import { useCommentsData, CommentActionType } from '@confluence/comments-data';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data';

import { InlineCommentPubSubEvents } from '../CommentPubSubEvents';
import {
	refreshCommentThread,
	handleAddedReplyInCommentThread,
	handleAddNewCommentThread,
} from '../utils/pubsubUtils';

type BasePubsubProps = {
	contentId: string;
	contentType?: string | null;
};

export type PubSubProps = BasePubsubProps & {
	triggerAnalyticsEvent?:
		| ((
				eventName: string,
				extraData?: {
					[key: string]: any;
				},
		  ) => Promise<void>)
		| CreateUIAnalyticsEvent;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
};

export const useEditorInlineCommentsPubSub = ({
	contentId,
	contentType,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
}: PubSubProps) => {
	const pubSubResourceType = contentType || 'page';

	const [
		{ orderedActiveAnnotationIdList },
		{
			addReplyToCommentThread,
			addNewCommentThreads,
			updateRemovedCommentIdsMap,
			updateCommentCount,
		},
	] = useCommentsData();
	const { userId: currentUserId } = useSessionData();
	const orderedActiveAnnotationIdListRef = useRef(orderedActiveAnnotationIdList);
	const { editorViewProps } = useAnnotations();

	const { annotations = [] } = editorViewProps || {};
	const selectedAnnotationId = annotations[0]?.id;

	useEffect(() => {
		orderedActiveAnnotationIdListRef.current = orderedActiveAnnotationIdList;
	}, [orderedActiveAnnotationIdList]);

	const handleOnPublicationEvent = useCallback(
		(event: string, markerRef: any) => {
			const emitter = getEditorAnnotationEventEmitter();
			emitter.emit(event, markerRef);
			const annotationIds = orderedActiveAnnotationIdListRef.current.map(
				(annotation) => annotation.annotationId,
			);

			if (triggerAnalyticsEvent) {
				// Updates ordered list of unresolved inline comments based on ADF to account for real-time changes (collaborative editing)
				void getEditorResolvedState({
					// the annotations won't already include the pubsub marker ref
					annotations: getSortedMarkerRefs([...annotationIds, markerRef], 'edit'),
					pageId: contentId,
					triggerAnalyticsEvent,
					updateUnresolvedInlineComment,
				});
			}
		},
		[contentId, triggerAnalyticsEvent, updateUnresolvedInlineComment],
	);

	const onPublicationEvent = useCallback(
		async (event: string, payload: any) => {
			const { inlineCommentRequestBody, accountId } = payload;
			const { markerRef, commentId, inlineCommentType } = inlineCommentRequestBody;

			// Don't do anything when the current user performs an action
			if (accountId === currentUserId && fg('dont_emit_pubsub_events_in_editor_when_self_event')) {
				return;
			}

			const isReply = inlineCommentType === 'REPLY';

			switch (event) {
				case InlineCommentPubSubEvents.InlineCommentUnresolved:
					if (fg('confluence-frontend-comments-panel')) {
						updateCommentCount &&
							updateCommentCount({
								parentMarkerRef: markerRef,
								actionType: CommentActionType.REOPEN_COMMENT_THREAD,
							});
					}
					handleOnPublicationEvent('unresolve', markerRef);
					break;
				case InlineCommentPubSubEvents.InlineCommentResolved:
					handleOnPublicationEvent('resolve', markerRef);

					if (fg('confluence-frontend-comments-panel')) {
						updateCommentCount &&
							updateCommentCount({
								parentMarkerRef: markerRef,
								actionType: CommentActionType.RESOLVE_COMMENT_THREAD,
							});
						updateRemovedCommentIdsMap({
							parentMarkerRef: markerRef,
							commentId,
							isReply,
							commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
						});
					}

					break;
				case InlineCommentPubSubEvents.InlineCommentCreated:
				case InlineCommentPubSubEvents.EditorInlineCommentCreated:
					if (fg('confluence-frontend-comments-panel')) {
						if (isReply) {
							void handleAddedReplyInCommentThread({
								contentId,
								markerRef,
								commentId,
								addReplyToCommentThread,
							});
						} else {
							void handleAddNewCommentThread({
								contentId,
								markerRef,
								addNewCommentThreads,
							});
						}
						updateCommentCount &&
							updateCommentCount({
								parentMarkerRef: markerRef,
								actionType: CommentActionType.CREATE_COMMENT,
							});
					} else {
						if (isReply && markerRef === selectedAnnotationId) {
							void refreshCommentThread(contentId, markerRef);
						}
					}

					handleOnPublicationEvent('create', markerRef);
					break;
				case InlineCommentPubSubEvents.InlineCommentDeleted:
					// both a parent comment and reply can be deleted
					if (fg('confluence-frontend-comments-panel')) {
						updateCommentCount &&
							updateCommentCount({
								parentMarkerRef: markerRef,
								actionType: CommentActionType.DELETE_COMMENT,
							});

						updateRemovedCommentIdsMap({
							parentMarkerRef: markerRef,
							commentId,
							isReply,
							commentActionType: CommentActionType.DELETE_COMMENT,
						});
					}
				default:
					break;
			}
		},
		[
			contentId,
			handleOnPublicationEvent,
			selectedAnnotationId,
			addNewCommentThreads,
			addReplyToCommentThread,
			updateRemovedCommentIdsMap,
			currentUserId,
			updateCommentCount,
		],
	);

	const register = useCallback(() => {
		void getPubSubClient().then((client) => {
			client
				.joinChannel(pubSubResourceType, contentId)
				.finally(() => {
					void client.subscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

					void client.subscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

					void client.subscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

					void client.subscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);

					if (fg('confluence-frontend-comments-panel')) {
						void client.subscribe(eventToAvi('deleted:inline_comment'), onPublicationEvent);
					}
				})
				.catch((error) => {
					throw new Error(`PubSub Web Client joinChannel ${JSON.stringify(error)}`);
				});
		});
	}, [contentId, onPublicationEvent, pubSubResourceType]);

	const unregister = useCallback(() => {
		void getPubSubClient().then((client) => {
			void client.leaveChannel(pubSubResourceType, contentId);
			void client.unsubscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

			void client.unsubscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

			void client.unsubscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

			void client.unsubscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);

			if (fg('confluence-frontend-comments-panel')) {
				void client.unsubscribe(eventToAvi('deleted:inline_comment'), onPublicationEvent);
			}
		});
	}, [contentId, onPublicationEvent, pubSubResourceType]);

	return useMemo(
		() => ({
			register,
			unregister,
			onPublicationEvent,
		}),
		[register, unregister, onPublicationEvent],
	);
};
